/**
 * EstimatorIntakeLog — admin view of every SkyQuote / instant estimator
 * attempt hitting the public intake endpoint.
 *
 * Populated by routes/estimator_intake.py which persists every hit
 * (success or rejection) with the raw body, parsed keys, and outcome.
 * Purpose: debug SkyQuote / Zapier payload mismatches without needing
 * backend log access.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Header } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  ArrowLeft, CheckCircle2, AlertCircle, RefreshCw, Loader2, Copy,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const OUTCOME_STYLES = {
  success: { icon: CheckCircle2, className: "text-emerald-600", label: "Success" },
  rejected_name_missing: { icon: AlertCircle, className: "text-rose-600", label: "Rejected · no name" },
  rejected_contact_missing: { icon: AlertCircle, className: "text-rose-600", label: "Rejected · no phone/email" },
  pending: { icon: Loader2, className: "text-slate-400", label: "Pending" },
};

export default function EstimatorIntakeLog() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const q = filter ? `?limit=100&outcome=${filter}` : `?limit=100`;
      const res = await axios.get(`${API}/estimator-intake/recent${q}`);
      setRows(res.data.attempts || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load log");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const copyRaw = (row) => {
    try {
      navigator.clipboard.writeText(row.raw_body_preview || "");
      toast.success("Raw body copied");
    } catch { toast.error("Copy failed"); }
  };

  const successCount = rows.filter(r => r.outcome === "success").length;
  const rejectCount = rows.filter(r => r.outcome?.startsWith("rejected")).length;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => navigate(-1)} data-testid="log-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900">Estimator Intake Log</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Every SkyQuote / Instant Estimate webhook hit, with the raw payload we received.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="log-refresh">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Refresh
          </Button>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <button
            onClick={() => setFilter("")}
            className={`rounded-lg border py-2.5 text-center transition-all ${!filter ? "bg-[#0F4C81] text-white border-[#0F4C81]" : "bg-white border-slate-200 text-slate-600"}`}
            data-testid="filter-all"
          >
            <p className="text-[10px] uppercase font-bold tracking-wider opacity-80">Total</p>
            <p className="text-2xl font-bold tabular-nums">{rows.length}</p>
          </button>
          <button
            onClick={() => setFilter("success")}
            className={`rounded-lg border py-2.5 text-center transition-all ${filter === "success" ? "bg-emerald-600 text-white border-emerald-600" : "bg-white border-emerald-200 text-emerald-700"}`}
            data-testid="filter-success"
          >
            <p className="text-[10px] uppercase font-bold tracking-wider opacity-80">Landed</p>
            <p className="text-2xl font-bold tabular-nums">{successCount}</p>
          </button>
          <button
            onClick={() => setFilter("rejected_name_missing")}
            className={`rounded-lg border py-2.5 text-center transition-all ${filter?.startsWith("rejected") ? "bg-rose-600 text-white border-rose-600" : "bg-white border-rose-200 text-rose-700"}`}
            data-testid="filter-rejected"
          >
            <p className="text-[10px] uppercase font-bold tracking-wider opacity-80">Rejected</p>
            <p className="text-2xl font-bold tabular-nums">{rejectCount}</p>
          </button>
        </div>

        {rows.length === 0 && !loading && (
          <Card>
            <CardContent className="py-16 text-center">
              <p className="text-slate-500 font-medium">No attempts logged yet.</p>
              <p className="text-xs text-slate-400 mt-2">
                If SkyQuote just fired and nothing shows here, the request never reached tideshub.com.
                Check: (1) URL is <span className="font-mono">https://tideshub.com/api/public/estimator-intake</span> (no www), (2) Cloudflare firewall isn't blocking Zapier IPs.
              </p>
            </CardContent>
          </Card>
        )}

        {rows.map((row) => {
          const style = OUTCOME_STYLES[row.outcome] || OUTCOME_STYLES.pending;
          const Icon = style.icon;
          return (
            <Card key={row.id} data-testid={`log-row-${row.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <Icon className={`w-5 h-5 flex-shrink-0 ${style.className}`} />
                    <div className="min-w-0">
                      <CardTitle className={`text-base ${style.className}`}>{style.label}</CardTitle>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {new Date(row.received_at).toLocaleString()} · {row.source_ip || "unknown ip"}
                      </p>
                    </div>
                  </div>
                  {row.extracted_name && (
                    <span className="text-sm font-semibold text-slate-700 flex-shrink-0">{row.extracted_name}</span>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-2 text-xs">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                  <p><span className="text-slate-400">Content-Type:</span> <span className="font-mono text-slate-700">{row.content_type || "(none)"}</span></p>
                  <p><span className="text-slate-400">Body length:</span> <span className="font-mono text-slate-700">{row.raw_body_length} bytes</span></p>
                  <p className="sm:col-span-2"><span className="text-slate-400">User-Agent:</span> <span className="font-mono text-slate-700 text-[11px] break-all">{row.user_agent || "(none)"}</span></p>
                  <p className="sm:col-span-2"><span className="text-slate-400">Parsed keys ({row.parsed_keys?.length || 0}):</span> <span className="font-mono text-slate-700 text-[11px]">{row.parsed_keys?.length ? row.parsed_keys.join(", ") : "(none)"}</span></p>
                </div>
                {row.raw_body_preview && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-slate-400">Raw body:</p>
                      <button onClick={() => copyRaw(row)} className="text-[10px] text-[#0F4C81] hover:underline flex items-center gap-1" data-testid={`log-copy-${row.id}`}>
                        <Copy className="w-3 h-3" />Copy
                      </button>
                    </div>
                    <pre className="text-[11px] font-mono text-slate-700 bg-slate-50 rounded p-2 whitespace-pre-wrap break-all border border-slate-100 max-h-40 overflow-y-auto">
                      {row.raw_body_preview}
                    </pre>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
